import { useEffect, useState } from 'react';

const API_BASE =
  process.env.REACT_APP_CHAT_API_BASE ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '');
const TOKEN_ENDPOINT =
  process.env.REACT_APP_CHAT_TOKEN_ENDPOINT ||
  (process.env.NODE_ENV === 'development' ? '/dev/token' : '/api/chat-token.php');

async function requestToken() {
  const endpoints = [TOKEN_ENDPOINT];

  // Vite moves to the next free port during local development. The website's
  // CRA proxy still points at :5173, so discover the shifted token service
  // when another local app already owns that port.
  if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_CHAT_TOKEN_ENDPOINT) {
    endpoints.push(
      ...Array.from({ length: 7 }, (_, index) =>
        `http://localhost:${5174 + index}/dev/token`,
      ),
    );
  }

  let lastError;
  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint);
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Token endpoint returned ${contentType || 'an unknown content type'}`);
      }

      const payload = await response.json();
      if (!response.ok || !payload.token) {
        throw new Error(payload.error || `Token request failed (${response.status})`);
      }
      return payload;
    } catch (caught) {
      lastError = caught;
    }
  }

  throw lastError || new Error('Could not find the Ask Guru token service');
}

function mountWidget(options) {
  if (window.AcpWidget) {
    window.AcpWidget.mount(options);
    return Promise.resolve();
  }

  const existing = document.querySelector('script[data-ask-guru-widget]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/widget.js`;
    script.async = true;
    script.dataset.askGuruWidget = 'true';
    script.dataset.apiBase = options.apiBase;
    script.dataset.token = options.token;
    script.dataset.brandName = options.brandName;
    script.dataset.greeting = options.greeting;
    script.dataset.accent = options.accent;
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.body.appendChild(script);
  });
}

const ChatBotWidget = () => {
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function initializeWidget() {
      try {
        const payload = await requestToken();

        if (cancelled) return;
        await mountWidget({
          apiBase: payload.apiBase || API_BASE,
          token: payload.token,
          brandName: 'Ask Guru',
          greeting: 'Hi! Ask me anything about Vedalytics.',
          accent: '#0f70f0',
        });
        if (!cancelled) {
          setReady(true);
          setError('');
        }
      } catch (caught) {
        if (!cancelled) setError(String(caught));
      }
    }

    initializeWidget();
    return () => {
      cancelled = true;
    };
  }, [retryCount]);

  if (ready) return null;

  return (
    <button
      type="button"
      onClick={() => setRetryCount((count) => count + 1)}
      aria-label={error ? 'Retry Ask Guru' : 'Ask Guru is loading'}
      title={error ? 'Ask Guru is temporarily unavailable. Click to retry.' : 'Ask Guru is loading'}
      style={{
        position: 'fixed',
        right: '20px',
        bottom: '20px',
        zIndex: 2147483000,
        minHeight: '48px',
        padding: '0 18px',
        border: 0,
        borderRadius: '999px',
        background: '#0f70f0',
        color: '#fff',
        font: '600 15px/1 system-ui, sans-serif',
        boxShadow: '0 8px 28px rgba(15, 112, 240, 0.3)',
        cursor: error ? 'pointer' : 'wait',
      }}
    >
      Ask Guru
    </button>
  );
};

export default ChatBotWidget;
